/**
 * QuestionSessionReport — the student-facing report shown atop the results
 * screen after a submitted assignment attempt. Synthesised server-side; this
 * renders it. See docs/FeatureDesigns/student-performance-report-diagnosis/question-session-report.md.
 *
 * Handles its own load states: `pending` → a quiet "preparing" strip, `failed`
 * / absent → renders nothing (the normal results summary carries the screen).
 */
import { useState } from 'react';
import { LatexText } from '@shared-ui/latex';
import { ThumbsRating, type ThumbsValue } from '../components/ThumbsRating';
import { Modal } from '../components/Modal';
import { Button } from '../components/Button';
import { perfTier } from '../scoring-labels';
import type {
    QuestionSessionReportState,
    QuestionSessionReportHeadline,
    ReportPoint,
    ReportFocus,
    ReportPointTrend,
    ReportFocusTrend,
} from './types';
import './QuestionSessionReport.css';

export interface QuestionSessionReportProps {
    report: QuestionSessionReportState | null | undefined;
    /** Headline numbers for the stat band (from the session summary). */
    headline?: QuestionSessionReportHeadline;
    /** True when the test has a marking scheme (shows the Marks stat). */
    hasMarking?: boolean;
    /** Persist the student's 👍/👎 on this report, plus the written reason once
     *  they give one. Called twice for a rated-then-explained flow: first with
     *  no comment (so the rating survives a skipped or abandoned prompt), then
     *  again with it. Omit and no rating row is shown — read-only hosts (admin
     *  preview, teacher view) pass nothing. */
    onRate?: (rating: ThumbsValue, comment?: string) => Promise<void>;
}

const TREND_META: Record<ReportPointTrend | ReportFocusTrend, { label: string; tone: string }> = {
    consistent:  { label: 'Consistent',        tone: 'good' },
    now_reliable:{ label: 'Now reliable',       tone: 'good' },
    improving:   { label: 'Improving',          tone: 'info' },
    recurring:   { label: 'Recurring',          tone: 'crit' },
    slipping:    { label: 'Slipped this time',  tone: 'warn' },
    new:         { label: 'New',                tone: 'new' },
};

function TrendChip({ trend, recurrenceCount }: { trend: ReportPointTrend | ReportFocusTrend; recurrenceCount?: number }) {
    const meta = TREND_META[trend];
    if (!meta) return null;
    const label = trend === 'recurring' && recurrenceCount ? `Recurring · ${recurrenceCount}×` : meta.label;
    return <span className={`qsr-chip qsr-chip--${meta.tone}`}>{label}</span>;
}

/** The LLM writes impact as a mid-sentence fragment ("cost 2 marks"); it is
 *  rendered as a standalone label, so it needs to read as a complete clause.
 *  Bare "Cost 2 marks" scans as an imperative — past tense is what's meant.
 *  Normalised here rather than in the prompt so reports already stored read
 *  the same way as new ones. */
function impactLabel(text: string): string {
    const withTense = text.replace(/^cost(?=\s)/i, 'Costed');
    return withTense.charAt(0).toUpperCase() + withTense.slice(1);
}

/**
 * The questions a point is drawn from, trailing the sentence that makes the
 * point rather than sitting on their own row. They are the end of that
 * sentence — "…incorrectly applied (Q1, Q2)" — so a separate line made the
 * reader hop to connect two halves of one thought.
 *
 * `lead` is a word that belongs with the refs ("From"), not a caption for the
 * card; anything describing the point itself stays out of the paragraph.
 */
function EvidenceRefs({ refs, lead }: { refs: string[]; lead?: string }) {
    if (!refs?.length) return null;
    return (
        <span className="qsr-ev">
            {lead && <span className="qsr-ev-lead">{lead}</span>}
            {refs.map((r) => (
                <span key={r} className="qsr-qref">{r}</span>
            ))}
        </span>
    );
}

/** Compact trajectory sparkline (server-supplied series). */
function Sparkline({ series }: { series: number[] }) {
    if (!series || series.length < 2) return null;
    const w = 72, h = 26, pad = 3;
    const min = Math.min(...series), max = Math.max(...series);
    const rng = (max - min) || 1;
    const pts = series.map((v, i) => {
        const x = pad + (i / (series.length - 1)) * (w - 2 * pad);
        const y = h - pad - ((v - min) / rng) * (h - 2 * pad);
        return [x, y] as const;
    });
    const line = pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
    const area = `${line} L${(w - pad).toFixed(1)} ${h - pad} L${pad} ${h - pad} Z`;
    const last = pts[pts.length - 1];
    const rising = series[series.length - 1] >= series[0];
    const cls = rising ? 'qsr-spark--up' : 'qsr-spark--down';
    return (
        <svg className={`qsr-spark ${cls}`} width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
            <path className="qsr-spark-area" d={area} />
            <path className="qsr-spark-line" d={line} fill="none" />
            <circle className="qsr-spark-dot" cx={last[0].toFixed(1)} cy={last[1].toFixed(1)} r={2.8} />
        </svg>
    );
}

function StrengthCard({ point }: { point: ReportPoint }) {
    return (
        <div className="qsr-card qsr-card--good">
            <div className="qsr-card-top">
                <div className="qsr-card-title"><LatexText text={point.title} /></div>
                <TrendChip trend={point.trend} />
            </div>
            <p className="qsr-card-body">
                <LatexText text={point.body} />
                <EvidenceRefs refs={point.evidence} lead="From" />
            </p>
        </div>
    );
}

function FocusCard({ point, rank }: { point: ReportFocus; rank: number }) {
    const crit = point.trend === 'recurring';
    return (
        <div className={`qsr-card qsr-card--focus${crit ? ' qsr-card--crit' : ''}`}>
            <div className="qsr-card-top">
                <span className="qsr-rank">{rank}</span>
                <div className="qsr-card-title"><LatexText text={point.title} /></div>
                <TrendChip trend={point.trend} recurrenceCount={point.recurrenceCount} />
            </div>
            <p className="qsr-card-body">
                <LatexText text={point.body} />
                <EvidenceRefs refs={point.evidence} />
            </p>
            {point.impact && (
                <div className="qsr-card-foot">
                    <span className="qsr-foot-label">{impactLabel(point.impact)}</span>
                </div>
            )}
        </div>
    );
}

function StatBand({ headline, hasMarking }: { headline?: QuestionSessionReportHeadline; hasMarking?: boolean }) {
    if (!headline) return null;
    const { score, questionsCorrect, questionsAttempted, questionsSkipped, totalQuestions, marksAwarded, maxMarks } = headline;
    const showMarks = hasMarking && typeof marksAwarded === 'number' && typeof maxMarks === 'number';
    return (
        <div className="stat-cards stat-cards--label-above qsr-stats">
            {typeof score === 'number' && (
                <div className="stat-card stat-card--metric" data-perf={perfTier(score * 10)}>
                    <span className="stat-card-label">Score</span>
                    <span className="stat-card-value">{score.toFixed(1)}<span className="stat-card-unit">/10</span></span>
                </div>
            )}
            {typeof questionsCorrect === 'number' && typeof questionsAttempted === 'number' && (
                <div className="stat-card stat-card--metric" data-perf={perfTier(questionsAttempted ? (questionsCorrect / questionsAttempted) * 100 : 0)}>
                    <span className="stat-card-label">Correct</span>
                    <span className="stat-card-value">{questionsCorrect}<span className="stat-card-unit">/{questionsAttempted}</span></span>
                </div>
            )}
            {showMarks && (
                <div className="stat-card stat-card--metric" data-perf={perfTier(maxMarks ? (marksAwarded! / maxMarks!) * 100 : 0)}>
                    <span className="stat-card-label">Marks</span>
                    <span className="stat-card-value">{marksAwarded}<span className="stat-card-unit">/{maxMarks}</span></span>
                </div>
            )}
            {typeof questionsAttempted === 'number' && typeof totalQuestions === 'number' && (
                <div className="stat-card stat-card--metric" data-perf="neutral">
                    <span className="stat-card-label">Attempted</span>
                    <span className="stat-card-value">{questionsAttempted}<span className="stat-card-unit">/{totalQuestions}</span></span>
                    {typeof questionsSkipped === 'number' && questionsSkipped > 0 && (
                        <span className="stat-card-sub">{questionsSkipped} skipped</span>
                    )}
                </div>
            )}
        </div>
    );
}

/** Optional follow-up after a rating: what made it (un)helpful. Skippable —
 *  a forced-comment gate is how you turn a one-tap signal into no signal. */
function ReasonPrompt({ rating, initialComment, onSubmit, onSkip }: {
    rating: ThumbsValue;
    initialComment?: string;
    onSubmit: (comment: string) => Promise<void>;
    onSkip: () => void;
}) {
    const [comment, setComment] = useState(initialComment ?? '');
    const [saving, setSaving] = useState(false);

    const send = async () => {
        const text = comment.trim();
        if (!text) { onSkip(); return; }
        setSaving(true);
        try { await onSubmit(text); } catch { onSkip(); } finally { setSaving(false); }
    };

    return (
        <Modal
            isOpen
            onClose={onSkip}
            title={rating === 'positive' ? 'What worked for you?' : 'What was off?'}
            size="sm"
            footer={
                <>
                    <Button variant="secondary" onClick={onSkip} disabled={saving}>Skip</Button>
                    <Button variant="primary" onClick={send} disabled={saving || !comment.trim()}>
                        {saving ? 'Sending…' : 'Send'}
                    </Button>
                </>
            }
        >
            <p className="qsr-reason-hint">
                {rating === 'positive'
                    ? 'Tell us what made this report useful — it helps us keep it that way.'
                    : 'Tell us what was wrong or unhelpful — we read every one of these.'}
            </p>
            <textarea
                className="qsr-reason-input"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Optional"
                rows={4}
                maxLength={2000}
                autoFocus
            />
        </Modal>
    );
}

export function QuestionSessionReport({ report, headline, hasMarking, onRate }: QuestionSessionReportProps) {
    const [open, setOpen] = useState(true);
    // Which rating is waiting for a written reason. Set AFTER the rating is
    // persisted, so closing this without typing still leaves the thumb recorded.
    const [reasonFor, setReasonFor] = useState<ThumbsValue | null>(null);

    if (!report) return null;

    if (report.status !== 'ready') {
        // 'pending' → a quiet "preparing" strip; 'failed' → render nothing.
        if (report.status === 'pending') {
            return (
                <section className="qsr" aria-busy="true">
                    <div className="qsr-pending">
                        <span className="qsr-spinner" aria-hidden="true" />
                        Preparing your report…
                    </div>
                </section>
            );
        }
        return null;
    }

    const { verdict, trajectory, strengths, focusAreas, nextStep } = report;

    return (
        <section className="qsr">
            <button type="button" className="qsr-header" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
                <span className="qsr-eyebrow">Your report</span>
                <span className={`qsr-collapse${open ? ' qsr-collapse--open' : ''}`} aria-hidden="true">▾</span>
            </button>

            {open && (
                <div className="qsr-body">
                    {verdict && (
                        <div className="qsr-verdict">
                            <div className="qsr-verdict-rail" aria-hidden="true" />
                            <p><LatexText text={verdict} /></p>
                        </div>
                    )}

                    <StatBand headline={headline} hasMarking={hasMarking} />

                    {trajectory && trajectory.series.length >= 2 && (
                        <div className="qsr-traj">
                            <Sparkline series={trajectory.series} />
                            <div className="qsr-traj-tx">
                                {trajectory.moduleName && <span className="qsr-traj-h">{trajectory.moduleName}</span>}
                                {trajectory.caption && <span className="qsr-traj-d"><LatexText text={trajectory.caption} /></span>}
                            </div>
                        </div>
                    )}

                    {strengths.length > 0 && (
                        <div className="qsr-section">
                            <div className="qsr-section-head"><h3>What's working</h3></div>
                            <div className="qsr-cards">
                                {strengths.map((s, i) => <StrengthCard key={i} point={s} />)}
                            </div>
                        </div>
                    )}

                    {focusAreas.length > 0 && (
                        <div className="qsr-section">
                            <div className="qsr-section-head"><h3>Where to focus next</h3><span className="qsr-section-hint">ranked by impact</span></div>
                            <div className="qsr-cards">
                                {focusAreas.map((f, i) => <FocusCard key={i} point={f} rank={i + 1} />)}
                            </div>
                        </div>
                    )}

                    {nextStep && (
                        <div className="qsr-next">
                            <div className="qsr-next-k">Your next step</div>
                            <div className="qsr-next-t"><LatexText text={nextStep} /></div>
                        </div>
                    )}

                    {onRate && (
                        <div className="qsr-rate">
                            <span className="qsr-rate-k">Was this helpful?</span>
                            <ThumbsRating
                                initialRating={report.feedback?.rating ?? null}
                                onRate={(r) => onRate(r)}
                                onAfterRate={(r) => setReasonFor(r)}
                                size="sm"
                            />
                        </div>
                    )}

                    {reasonFor && (
                        <ReasonPrompt
                            rating={reasonFor}
                            initialComment={report.feedback?.comment}
                            onSubmit={async (comment) => { await onRate!(reasonFor, comment); setReasonFor(null); }}
                            onSkip={() => setReasonFor(null)}
                        />
                    )}
                </div>
            )}
        </section>
    );
}
